import { defineStore } from 'pinia'
import { isEqual, uniqWith } from 'lodash-es'
import getBrowserFingerprint from 'get-browser-fingerprint'
import { md5 } from 'js-md5'

import {
  getAddress,
  getInfo,
  getToken,
  removeAddress,
  removeInfo,
  removeToken,
  setAddress,
  setInfo,
  setToken,
} from '@/utils/auth'
import type { UserInfo } from '@/types'
import type { RevenueStatistic } from '@/api'

function getExtendedFingerprint() {
  const baseFingerprint = getBrowserFingerprint({
    hardwareOnly: true,
  })
  const additionalInfo = [
    navigator.userAgent, // 用户代理字符串
    navigator.language, // 语言
    screen.width, // 屏幕宽度
    screen.height, // 屏幕高度
    Intl.DateTimeFormat().resolvedOptions().timeZone, // 时区
  ]
  return `fp_${md5(String(`${baseFingerprint}_${additionalInfo.join('_')}`))}`
}

export const useUserStore = defineStore('user', {
  state: (): {
    info: UserInfo | undefined
    address: string | undefined
    token: string | undefined | null
    musicList: Array<any>
    playUrl: string
    isPlaying: boolean
    isShowMusicPlayer: boolean
    scrollTop: number
    isShowMessage: boolean
    isReadState: boolean
    userPoints: number
    chainId: number
    userBalance: any
    walletLoading: boolean
    messagePlacement: string
    lang: string
    messageCount: any
    viewHandle: any
    currentTime: number
    musicInfo: {
      isPlaying: boolean
      duration: number
      playUrl: string
      musicList: Array<any>
      isShowMusicPlayer: boolean
    }
    agoraChatInfo: {
      agoraChatMessage: any
      chatLoginState: boolean
      agoraChatId: string
      networkStatus: boolean
      readedChannelMessageId: string
      tempConversations: any
    }
    revenueStatistic: Partial<RevenueStatistic> & { loading?: boolean; loaded?: boolean }
    newMsgInfo: {
      sendTypes: number[]
      receiptUserCodeList: string[]
    }
    payFixedFee: number
    fcmToken: string
    userAgentKey: string
    homeScrollTop: number
  } => {
    return {
      info: getInfo(),
      address: getAddress(),
      token: getToken(),
      musicList: [],
      playUrl: '',
      isPlaying: false,
      isShowMusicPlayer: false,
      scrollTop: 0,
      isShowMessage: false,
      isReadState: false,
      userBalance: {
        ethValue: 0,
        usdValue: 0,
        usdRate: 0, // 美金转ETH汇率
        usdToStarsRate: 0, // 美金转Stars的汇率
        usdtValue: 0,
        solToUsdRate: 0,
      },
      revenueStatistic: {
        loading: true,
        loaded: false,
      },
      userPoints: 0,
      chainId: 5,
      walletLoading: false,
      messagePlacement: 'bottom-right',
      lang: 'en',
      messageCount: {
        message: 0,
        notification: 0,
      },
      viewHandle: [],
      currentTime: 0,
      musicInfo: {
        isPlaying: false,
        duration: 0,
        playUrl: '',
        musicList: [],
        isShowMusicPlayer: false,
      },
      agoraChatInfo: {
        agoraChatMessage: {},
        chatLoginState: false,
        agoraChatId: '',
        networkStatus: true,
        readedChannelMessageId: '',
        tempConversations: { conversations: [], totalPages: 0 },
      },
      newMsgInfo: {
        sendTypes: [],
        receiptUserCodeList: [],
      },
      payFixedFee: 0, // 支付固定费用
      fcmToken: '',
      userAgentKey: '',
      homeScrollTop: 0,
    }
  },
  getters: {
    isLogin: (state: any): boolean => {
      if (!state.userAgentKey) {
        state.userAgentKey = getExtendedFingerprint()
      }
      // 完全登录时  用户信息和钱包地址都存在
      return !!state.info || !!Local.get('info') || !!state.address || !!Local.get('address')
    },
    isCreator: (state: any): boolean => {
      const { info } = state
      return !!info?.creatorDomain
    },
  },
  actions: {
    INIT(res: any) {
      this.SET_ADDRESS(res.address)
      this.SET_TOKEN(res.token)
      this.SET_INFO(res.info)
      if ('PushManager' in window) {
        const { requestNotificationPermission } = usePushManager()
        requestNotificationPermission()
      }
    },
    SET_INFO(data: any) {
      if (!data.imageDisplayRatio) {
        data.imageDisplayRatio = 'blur-15px'
      }
      this.info = data
      setInfo(data)
      Local.set('info', data)
    },
    SET_TOKEN(data: string) {
      this.token = data
      setToken(data)
    },
    CLEAR_INFO() {
      this.info = undefined
      removeInfo()
    },
    CLEAR_TOKEN() {
      this.token = ''
      removeToken()
    },
    SET_ADDRESS(data: string) {
      setAddress(data)
      this.address = data
    },
    CLEAR_ADDRESS() {
      this.address = ''
      removeAddress()
      Cookie.remove('domain')
      Cookie.remove('Authorization')
      Cookie.remove('BMS-Authorization')
      Cookie.remove('creatorDomain')
      Cookie.remove('info')
      Local.remove('domain')
      Local.remove('Authorization')
      Local.remove('BMS-Authorization')
      Local.remove('creatorDomain')
      Local.remove('info')
    },
    CLEAR_USER() {
      this.CLEAR_INFO()
      this.CLEAR_TOKEN()
      this.CLEAR_ADDRESS()
      window.location.href = '/'
    },
    async LOGOUT() {
      // this.CLEAR()
      const _loadingInstance = LoadingView.getInstance()
      try {
        _loadingInstance.open({
          content: 'Logout...',
        })
        if ('PushManager' in window && this.fcmToken) {
          const { unsubscribeFromNotifications } = usePushManager()
          await unsubscribeFromNotifications()
        }
        this.CLEAR()
      } catch (error) {
        console.error(error)
      } finally {
        _loadingInstance.close()
      }
    },
    CLEAR() {
      this.isShowMessage = false
      this.isReadState = false
      this.CLEAR_USER()
      Session.clear()
    },
    SET_MUSIC_LIST(data: any) {
      this.musicList.push(data)
      this.musicList = uniqWith(this.musicList, isEqual)
      this.musicInfo.musicList = this.musicList
      Local.set('musicList', JSON.stringify(this.musicList))
    },
    SET_MUSIC_INFO(data: any) {
      this.musicInfo = {
        ...this.musicInfo,
        ...data,
      }
      data.current && this.SET_MUSIC_LIST(data.current)
    },
    SET_CURRENT_TIME(data: number) {
      this.currentTime = data
    },
    async GET_USER_INFO(address: string) {
      const res = await baseService.profileInfo({
        addressOrDomain: address,
      })
      this.SET_INFO(res)
    },
    async GET_PAY_FIXED_FEE() {
      if (this.payFixedFee) return
      const res = await walletService.getPayFixedFee()
      this.payFixedFee = res
    },
    SHOW_MUSIC(data: boolean) {
      this.isShowMusicPlayer = data
    },
    SET_SCROLLTOP(data: number) {
      this.scrollTop = data
    },
    SET_MESSAGESTATUS(data: any) {
      this.isShowMessage = data.isShowMessage
      this.isReadState = data.isReadState
    },
    SET_USER_BALANCE(res: any) {
      const { formatPriceToFixed } = useExchangeRate()
      const { scientificToNumber } = useMath()
      const ethValue = scientificToNumber(res.ethValue)
      this.userBalance = {
        ethValue: formatPriceToFixed(ethValue, ethValue > 0.000001 ? 6 : 14),
        usdValue: formatPriceToFixed(Number(res.usdValue)),
        usdRate: res.usdRate,
        usdToStarsRate: res.usdToStarsRate,
        usdtValue: formatPriceToFixed(Number(res.usdtValue)),
        solToUsdRate: res.solToUsdRate,
      }
    },
    async SET_BALANCE_INFO() {
      try {
        if (!this.info?.address) return
        const [res, solToUsdRate] = await Promise.all([
          walletService.balanceQuery({
            address: this.info?.address,
            chain: this.chainId,
          }),
          walletService.queryExchangeRate({
            targetCurrency: 'sol',
          }),
        ])
        this.SET_USER_BALANCE({ ...res, solToUsdRate })
      } catch (error) {
        console.error(error)
      }
    },
    async FETCH_REVENUE_STATISTIC() {
      this.revenueStatistic.loading = true
      try {
        const revenue = await statisticService.getPayoutsBalance()
        Object.assign(this.revenueStatistic, {
          ...revenue,
          loading: true,
        })
      } catch (error) {
        console.error(error)
      } finally {
        this.revenueStatistic.loading = false
        this.revenueStatistic.loaded = true
      }
    },
    SET_USERPOINTS(data: any) {
      this.userPoints = data
    },
    SET_CHAIN_ID(data: any) {
      this.chainId = data
    },
    SET_WALLET_LOADING(data: boolean) {
      this.walletLoading = data
    },
    SWITCH_PLACEMENT(placement: string) {
      this.messagePlacement = placement || 'bottom-right'
    },
    SET_I18N(lang: string) {
      this.lang = lang
      Local.set('LANG', lang)
    },
    async SET_MESSAGE_NOTIFICATION(isSeller?: boolean) {
      try {
        const [unreadChatNum, notification] = await Promise.all([
          messageService.unreadChatNum(),
          messageService.notificationStatistic(
            {},
            isSeller ? { headers: { isSeller: true } } : null,
          ),
        ])
        this.messageCount = {
          message: unreadChatNum || 0,
          notification,
        }
      } catch (error) {
        console.error(error)
      }
    },
    SET_VIEW_HANDLE(data: any) {
      this.viewHandle = data
    },
    // agora：修改消息登录状态
    CHANGE_CHAT_LOGIN_STATUS(status: boolean) {
      this.agoraChatInfo.chatLoginState = status
    },
    // agora：添加新消息
    CREATE_NEW_MESSAGE(message: any) {
      this.agoraChatInfo.agoraChatMessage = message
      this.agoraChatInfo.agoraChatId = message.id
    },
    // agora：消息网络状态
    CHANGE_NETWORK_STATUS(status: boolean) {
      this.agoraChatInfo.networkStatus = status
    },
    // agora：已读会话回执id
    CHANGE_CHANNEL_MESSAGE_ID(id: string) {
      this.agoraChatInfo.readedChannelMessageId = id
    },
    // agora: 临时会话列表
    SET_TEMP_CONVERSATIONS(list: Array<any>, totalPages: number) {
      this.agoraChatInfo.tempConversations = { conversations: list, totalPages }
    },
    SET_NEW_MSGINFO(info: any) {
      this.newMsgInfo = info
    },
    SET_FCM_TOKEN(token: string) {
      this.fcmToken = token
    },
    SET_HOME_SCROLL_TOP(scrollTop: number) {
      this.homeScrollTop = scrollTop
    },
  },
  persist: {
    enabled: true,
    strategies: [
      {
        key: 'user',
        storage: localStorage,
      },
    ],
  },
})
